import { useEffect, useMemo, useState } from "react";
import {
  Button as BPButton,
  FormGroup,
  MenuItem,
  NumericInput,
  Switch,
  Tooltip,
} from "@blueprintjs/core";
import { Suggest, ItemPredicate, ItemRendererProps } from "@blueprintjs/select";
import {
  FORM_WIDTH,
  INavigationProps,
  configContainerStyle,
  screenTitleStyle,
} from "./CStoreEditor";
import { Button } from "components/Button";
import { Colors } from "design/colors";
import {
  CStore,
  Op,
  OpType,
  StoredTag,
} from "cstore/cstore";

import { useLocalStorage } from "usehooks-ts";

type TagItem = {
  id: number;
  name: string;
  disabled: boolean;
};

// Minimum difference in numeric values required to count as modified
const FLOAT_DELTA = 1e-11;

type ModifyOpsByTag = { [ tagId: number ]: ( Op | undefined ) };

interface TagValueEditRowProps {
  tagId: number;
  cstore: CStore;
  modifyOpsByTag: ModifyOpsByTag;
  setModifyOpsByTag: React.Dispatch<ModifyOpsByTag>;
  unpin: ( tagId: number ) => void;
}

const TagValueEditRow = ( props: TagValueEditRowProps ) => {
  const {
    tagId,
    cstore,
    modifyOpsByTag,
    setModifyOpsByTag,
    unpin,
  } = props;

  const tag = cstore.storedTags.find( (st) => st.tagId === tagId );
  if (!tag) return null; // Should not be possible

  const def = tag.getTagDefinition( cstore.allTags );
  if (!def) return null; // Should not be possible

  const tagIdAndName = `${ tagId }. ${ def.name }`;
  const elementId = `tag-${ tagId }-value`;

  const originalValue = tag.getData();

  const onNumericValueChange = ( value: number ) => {
    if ( Math.abs( value - ( originalValue as number ) ) > FLOAT_DELTA ) {
      const modifyOp: Op = {
        type: OpType.UpdateTag,
        id: tagId,
        value,
      };
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: modifyOp } );
    } else {
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: undefined } );
    }
  };

  const onBooleanValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    if ( value !== ( originalValue as boolean ) ) {
      const modifyOp: Op = {
        type: OpType.UpdateTag,
        id: tagId,
        value,
      };
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: modifyOp } );
    } else {
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: undefined } );
    }
  };
  const onStringValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if ( value !== ( originalValue as string ) ) {
      const modifyOp: Op = {
        type: OpType.UpdateTag,
        id: tagId,
        value,
      };
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: modifyOp } );
    } else {
      setModifyOpsByTag( { ...modifyOpsByTag, [tagId]: undefined } );
    }
  };

  let inputElement;
  switch ( typeof originalValue ) {
    case "number":
      inputElement = (
        <NumericInput id={ elementId } onValueChange={ onNumericValueChange } defaultValue={ originalValue } />
      );
      break;
    case "boolean":
      inputElement = (
        <Switch id={ elementId } onChange={ onBooleanValueChange } defaultChecked={ originalValue } />
      );
      break;
    case "string":
      inputElement = (
        <input id={ elementId } type="text" onChange={ onStringValueChange } defaultValue={ originalValue } />
      );
      break;
    default:
      console.error( "Unimplemented value type for tag", tagIdAndName ); // TODO: support bigint
      return null;
  }

  return (
    <tr>
      <td>
        <FormGroup
          label={ tagIdAndName }
          labelFor={ elementId }
          inline={ true }
          labelInfo={ def.units }
          style={{ justifyContent: "space-between" }}
        >
          { inputElement }
        </FormGroup>
      </td>
      <td>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Tooltip content="Remove the ID for this tag from your pinned tag ID list">
            <BPButton
              icon="delete"
              onClick={ () => unpin( tagId ) }
              style={{ backgroundColor: Colors.RED, borderRadius: 16 }}
            />
          </Tooltip>
          <div style={{ height: 16 }} />
        </div>
      </td>
    </tr>
  );
  return null;
};

// https://github.com/palantir/blueprint/blob/a93381f6cba05e6ef62e18802d03c9e98beab196/packages/select/src/__examples__/films.tsx#L228
function highlightText(text: string, query: string) {
  const escapeRegExpChars = (text: string): string => text.replace(/([.*+?^=!:${}()|[\]/\\])/g, "\\$1");
  let lastIndex = 0;
  const words = query
    .split(/\s+/)
    .filter(word => word.length > 0)
    .map(escapeRegExpChars);
  if (words.length === 0) {
    return [text];
  }
  const regexp = new RegExp(words.join("|"), "gi");
  const tokens: React.ReactNode[] = [];
  while (true) {
    const match = regexp.exec(text);
    if (!match) {
      break;
    }
    const length = match[0].length;
    const before = text.slice(lastIndex, regexp.lastIndex - length);
    if (before.length > 0) {
      tokens.push(before);
    }
    lastIndex = regexp.lastIndex;
    tokens.push(<strong key={lastIndex}>{match[0]}</strong>);
  }
  const rest = text.slice(lastIndex);
  if (rest.length > 0) {
    tokens.push(rest);
  }
  return tokens;
}

export const AllSettingsScreen = (
  props: INavigationProps & {
    cstore: CStore,
    setAllSettingsModifyOps: React.Dispatch<Op[]>,
  }
) => {
  const {
    cstore,
    setAllSettingsModifyOps,
    next,
    prev,
  } = props;
  const [ pinnedTagIds, setPinnedTagIds ] = useLocalStorage<number[]>( "pinnedTagIds", [] );
  const [ selectedTagId, setSelectedTagId ] = useState<number | undefined>();
  const [ hideSuggestQuery, setHideSuggestQuery ] = useState<boolean>( false ); // Work around the inability to clear the Suggest query
  const [ modifyOpsByTag, setModifyOpsByTag ] = useState<ModifyOpsByTag>( {} );

  const inputValueRenderer = ( item: TagItem ) => `${ item.id }. ${ item.name }`;
  const itemDisabled = ( item: TagItem ) => item.disabled;
  const noResults = <MenuItem disabled={true} text="No results." roleStructure="listoption" />;

  // List of all cstore tags user sees in the box
  const tagItems: TagItem[] = useMemo(() => {
    return cstore.storedTags
      .filter( ( tag ) => {
        const def = tag.getTagDefinition( cstore.allTags );
        // Filter out: terminator, missing tags.toml definition, and deprecated tags
        return !tag.isTerminator() && def && !def.deprecated;
      } )
      .map( ( tag: StoredTag ): TagItem => {
          const def = tag.getTagDefinition( cstore.allTags );
          return {
            id: tag.tagId,
            name: def?.name ?? "",
            disabled: pinnedTagIds.includes( tag.tagId ), // Disallow adding more than once
          };
      });
  }, [cstore, pinnedTagIds]);

  useEffect( () => {
    setAllSettingsModifyOps( Object.values( modifyOpsByTag ).filter( ( value ) => isDefined( value ) ) as Op[] );
  }, [ modifyOpsByTag, setAllSettingsModifyOps ] );

  const onItemSelect = ( item: TagItem ) => setSelectedTagId( item.id );

  const itemRenderer = ( item: TagItem, rendererProps: ItemRendererProps ): JSX.Element => {
    const { handleClick, handleFocus, modifiers, query, ref } = rendererProps;
    return (
      <MenuItem
        selected={item.id === selectedTagId}
        onClick={ handleClick }
        onFocus={ handleFocus }
        active={ modifiers.active }
        disabled={ modifiers.disabled }
        ref={ ref }
        key={ `menu-${ item.id }` }
        text={ highlightText( `${ item.id }. ${ item.name }`, query ) }
      />
    );
  };

  const itemPredicate: ItemPredicate<TagItem> = (query, item, _index, exactMatch) => {
    const normalizedName = item.name.toLowerCase();
    const normalizedQuery = query.toLowerCase();
    if (exactMatch) {
      return normalizedName === normalizedQuery;
    } else {
      // All searchable data in item should be below
      return `${ item.id }. ${ normalizedName }`.indexOf( normalizedQuery ) >= 0;
    }
  };

  const selectTag = () => {
    if ( selectedTagId === undefined ) return;
    setPinnedTagIds( (pinnedTagIds) => [ ...pinnedTagIds, selectedTagId ] );
    setSelectedTagId( undefined );
    setHideSuggestQuery( true );
  };

  const unpin = ( tagId: number ) => {
    setPinnedTagIds( (pinnedTagIds) => {
      return pinnedTagIds.filter( (id) => id !== tagId );
    } );
  };

  return (
    <>
      <div style={configContainerStyle}>
        <div style={screenTitleStyle}>
          All Settings (Advanced)
        </div>
        <div style={{ display: "flex", flexDirection: "row", width: FORM_WIDTH*1.15 }}>
          <div style={{ width: FORM_WIDTH*0.85 }} onFocus={ () => setHideSuggestQuery( false ) } >
            <Suggest
              inputValueRenderer={ inputValueRenderer }
              itemRenderer={ itemRenderer }
              items={ tagItems }
              onItemSelect={ onItemSelect }
              itemDisabled={ itemDisabled }
              itemPredicate={ itemPredicate }
              resetOnClose={ true }
              resetOnSelect={ true }
              fill={ true }
              selectedItem={ hideSuggestQuery ? null : undefined }
              noResults={ noResults }
              />
            </div>

          <Button
            backgroundColor={ Colors.BUTTON_GREEN }
            textColor={ Colors.WHITE }
            disabled={ selectedTagId === undefined }
            onClick={ selectTag }
          >
            Select Tag
          </Button>
        </div>
        <div style={{ height: 12 }} />
        <table>
          <tbody>
            { pinnedTagIds.map( ( tagId ) => (
              <TagValueEditRow key={tagId} tagId={tagId} cstore={cstore}
                modifyOpsByTag={ modifyOpsByTag }
                setModifyOpsByTag={ setModifyOpsByTag }
                unpin={ unpin } />
            ) ) }
          </tbody>
        </table>
        <div style={{ height: 32 }} />
        {/* Back and next buttons */}
        <div style={{ display: "flex", width: FORM_WIDTH }}>
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.WHITE} textColor={Colors.BLACK}
              onClick={prev} disabled={prev === undefined}
            >
              Back
            </Button>
          </div>
          <div style={{ width: 8 }} />
          <div style={{ flex: 1 }}>
            <Button
              backgroundColor={Colors.BUTTON_GREEN} textColor={Colors.WHITE}
              onClick={next} disabled={next === undefined}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
